import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BulkEntryGrid } from '../components/BulkEntryGrid';
import { useCompany } from '../contexts/CompanyContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, FileText } from 'lucide-react';

const UNIT_OPTIONS = [
  { value: 'Meters', label: 'Meters' },
  { value: 'Bales', label: 'Bales' },
  { value: 'Thans', label: 'Thans' },
  { value: 'Kg', label: 'Kg' },
  { value: 'Pieces', label: 'Pieces' },
];

const BulkGreyOrders = () => {
  const navigate = useNavigate();
  const { activeCompany } = useCompany();
  const [suppliers, setSuppliers] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [subqualities, setSubqualities] = useState([]);
  const [items, setItems] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [s, q, sq, it] = await Promise.all([
          api.get('/suppliers'),
          api.get('/qualities'),
          api.get('/subqualities'),
          api.get('/items'),
        ]);
        setSuppliers(s.data);
        setQualities(q.data);
        setSubqualities(sq.data);
        setItems(it.data);
      } catch {
        toast.error('Failed to load master data');
      }
    };
    fetchData();
  }, [activeCompany?.company_id]);

  const supplierOptions = useMemo(
    () => suppliers.map(s => ({ value: s.supplier_id, label: `${s.name} - ${s.city}` })),
    [suppliers]
  );

  const qualityOptions = useMemo(
    () => qualities.map(q => ({ value: q.quality_id, label: q.quality_name })),
    [qualities]
  );

  const supplierMap = useMemo(() => {
    const m = {};
    suppliers.forEach(s => { m[s.supplier_id] = s; });
    return m;
  }, [suppliers]);

  const qualityMap = useMemo(() => {
    const m = {};
    qualities.forEach(q => { m[q.quality_id] = q; });
    return m;
  }, [qualities]);

  const itemOptions = useMemo(
    () => items.map(i => ({ value: i.item_id, label: i.item_name })),
    [items]
  );

  const getFinishQualityOptions = useCallback((row) => {
    if (!row.quality_id) return [];
    return subqualities
      .filter(sq => (sq.quality_ids?.length ? sq.quality_ids : [sq.quality_id]).includes(row.quality_id))
      .map(sq => ({ value: sq.subquality_id, label: sq.subquality_name }));
  }, [subqualities]);

  const columns = useMemo(() => [
    {
      key: 'order_date', label: 'Order Date', required: true, type: 'date', width: '140px',
      defaultValue: new Date().toISOString().split('T')[0],
    },
    {
      key: 'supplier_id', label: 'Supplier', required: true, type: 'select', width: '200px',
      options: supplierOptions, placeholder: 'Select supplier',
      onCellChange: (value) => {
        const sup = supplierMap[value];
        return { city: sup?.city || '' };
      },
    },
    {
      key: 'city', label: 'City', required: true, width: '120px', readOnly: true,
      placeholder: 'Auto-filled',
    },
    {
      key: 'item_id', label: 'Item', required: true, type: 'select', width: '160px',
      options: itemOptions, placeholder: 'Select item',
    },
    {
      key: 'quality_id', label: 'Quality', required: true, type: 'select', width: '170px',
      options: qualityOptions, placeholder: 'Select quality',
      onCellChange: (value) => {
        const qual = qualityMap[value];
        return { subquality_id: '', order_unit: qual?.default_unit || '' };
      },
    },
    {
      key: 'subquality_id', label: 'Finish Quality', required: true, type: 'select', width: '170px',
      placeholder: 'Select finish quality',
      getOptions: getFinishQualityOptions,
    },
    {
      key: 'order_unit', label: 'Unit', required: true, type: 'select', width: '110px',
      options: UNIT_OPTIONS, placeholder: 'Unit',
    },
    {
      key: 'ordered_quantity', label: 'Quantity', required: true, type: 'number', width: '110px',
      placeholder: '5000',
      validate: (v) => {
        const num = parseFloat(v);
        if (isNaN(num) || num <= 0) return 'Quantity must be > 0';
        return null;
      },
    },
    {
      key: 'rate', label: 'Rate', required: true, type: 'number', width: '100px',
      placeholder: '85.50',
      validate: (v) => {
        const num = parseFloat(v);
        if (isNaN(num) || num <= 0) return 'Rate must be > 0';
        return null;
      },
    },
    {
      key: 'remarks', label: 'Remarks', width: '160px', placeholder: 'Optional',
    },
  ], [supplierOptions, qualityOptions, itemOptions, supplierMap, qualityMap, getFinishQualityOptions]);

  const itemMap = useMemo(() => {
    const m = {};
    items.forEach(i => { m[i.item_id] = i; });
    return m;
  }, [items]);

  const handleSave = async (rows) => {
    const processed = rows.map(r => ({
      ...r,
      ordered_quantity: parseFloat(r.ordered_quantity),
      rate: parseFloat(r.rate),
      item: itemMap[r.item_id]?.item_name || '',
    }));
    const res = await api.post('/bulk/grey-orders', { rows: processed });
    if (res.data.success_count > 0) {
      toast.success(`${res.data.success_count} grey order(s) created successfully`);
    }
    if (res.data.error_count > 0) {
      toast.error(`${res.data.error_count} row(s) had errors`);
    }
    return res.data;
  };

  return (
    <div className="space-y-6" data-testid="bulk-grey-orders-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="bulk-grey-orders-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Bulk Add Grey Orders</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Add multiple grey orders at once · <span className="theme-text font-medium">{activeCompany?.name}</span>
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/grey-orders')} className="gap-1.5">
          <FileText className="h-4 w-4" /> View Orders
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          <BulkEntryGrid
            columns={columns}
            onSave={handleSave}
            entityName="Grey Order"
            testIdPrefix="bulk-go"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default BulkGreyOrders;
